import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CircleHelp,
  BookX,
  Users,
  Repeat,
  Frown,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService, {
  type KGReportCategory,
  type KGSummaryResponse,
  type KGRawFindingResponse,
} from '@/lib/api/knowledgeGaps';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const CATEGORY_ICON_MAP: Record<string, { icon: LucideIcon; label: string }> = {
  unanswered_question: { icon: CircleHelp, label: 'Unanswered Questions' },
  missing_knowledge: { icon: BookX, label: 'Missing Knowledge' },
  segment_mismatch: { icon: Users, label: 'Segment Mismatch' },
  repeated_question: { icon: Repeat, label: 'Repeated Questions' },
  user_dissatisfaction: { icon: Frown, label: 'User Dissatisfaction' },
};

const SORT_OPTIONS = [
  { value: 'occurrence_count_desc', label: 'Most Occurrences First' },
  { value: 'first_seen_desc', label: 'Most Recent First' },
] as const;

type SortBy = (typeof SORT_OPTIONS)[number]['value'];

const PAGE_SIZE = 20;

// ─── Helpers ────────────────────────────────────────────────────────────────

function getCategoryMeta(category: string): { icon: LucideIcon; label: string } {
  if (category in CATEGORY_ICON_MAP) {
    return CATEGORY_ICON_MAP[category];
  }
  return {
    icon: AlertCircle,
    label: category
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function GapDetail({
  orgId,
  summary,
  onResolve,
  onDismiss,
  isResolving,
  isDismissing,
}: {
  orgId: string;
  summary: KGSummaryResponse;
  onResolve: () => void;
  onDismiss: () => void;
  isResolving: boolean;
  isDismissing: boolean;
}) {
  const meta = getCategoryMeta(summary.category);
  const isMutating = isResolving || isDismissing;

  const { data: findings, isLoading: findingsLoading } = useQuery({
    queryKey: ['knowledge-gaps-findings', orgId, summary.id],
    queryFn: async () => {
      const res = await knowledgeGapsService.getSummaryFindings(orgId, summary.id);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load evidence');
      }
      return res.data;
    },
    enabled: !!summary.id,
  });

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b card-border px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{summary.topic}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {meta.label}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              disabled={isMutating}
              onClick={onResolve}
            >
              {isResolving ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-1.5" />
              )}
              Mark Resolved
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isMutating}
              onClick={onDismiss}
            >
              {isDismissing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-1.5" />
              )}
              Dismiss
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5 space-y-6">
        {/* Attributes */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Attributes
          </h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-muted-foreground">Category</span>
            <span>{meta.label}</span>
            <span className="text-muted-foreground">Occurrences</span>
            <span>{summary.occurrence_count}</span>
            <span className="text-muted-foreground">First Seen</span>
            <span>{formatDate(summary.first_seen_at)}</span>
            <span className="text-muted-foreground">Last Seen</span>
            <span>{formatDate(summary.last_seen_at)}</span>
          </div>
        </section>

        {/* Gap Explanation */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Gap Explanation
          </h3>
          <p className="text-sm">{summary.summary}</p>
        </section>

        {/* Evidence */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Evidence ({findings?.length ?? '...'})
          </h3>
          {findingsLoading && (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          )}
          {!findingsLoading && findings && findings.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No raw evidence available.
            </p>
          )}
          {findings?.map((finding: KGRawFindingResponse, idx: number) => (
            <div
              key={finding.id}
              className="rounded-md border border-border bg-muted/30 p-4 space-y-2 mb-3 last:mb-0"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>Evidence #{idx + 1}</span>
                <Badge variant="secondary" className="font-mono text-xs">
                  {finding.trace_id.slice(0, 12)}...
                </Badge>
                {finding.session_id && (
                  <Link
                    to={`/analytics/sessions?sessionId=${finding.session_id}`}
                    className="text-xs text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    View Session →
                  </Link>
                )}
              </div>
              {finding.finding_data.evidence && (
                <p className="text-sm">{finding.finding_data.evidence}</p>
              )}
              {finding.finding_data.details && (
                <p className="text-sm text-muted-foreground">
                  {finding.finding_data.details}
                </p>
              )}
            </div>
          ))}
        </section>

        {/* Suggested Action */}
        {summary.suggested_action && (
          <section>
            <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
              Suggested Action
            </h3>
            <p className="text-sm">{summary.suggested_action}</p>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface ActiveGapsTabProps {
  orgId: string;
}

export function ActiveGapsTab({ orgId }: ActiveGapsTabProps) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortBy>('occurrence_count_desc');
  const [page, setPage] = useState(1);
  const [selectedSummaryId, setSelectedSummaryId] = useState<string | null>(null);

  // ── Queries ─────────────────────────────────────────────────────────────

  const {
    data: report,
    isError: reportError,
    refetch: refetchReport,
  } = useQuery({
    queryKey: ['knowledge-gaps-report', orgId],
    queryFn: async () => {
      const res = await knowledgeGapsService.getReport(orgId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load report');
      }
      return res.data;
    },
    enabled: !!orgId,
  });

  const {
    data: summariesData,
    isLoading: summariesLoading,
    isError: summariesError,
    refetch: refetchSummaries,
  } = useQuery({
    queryKey: ['knowledge-gaps-summaries', orgId, category, sortBy, page],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        status: 'active',
        sort_by: sortBy,
        page,
        limit: PAGE_SIZE,
      };
      if (category !== 'all') {
        params.category = category;
      }
      const res = await knowledgeGapsService.getSummaries(orgId, params as any);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load summaries');
      }
      return res.data;
    },
    enabled: !!orgId,
    staleTime: 0,
  });

  // ── Mutations ───────────────────────────────────────────────────────────

  const resolveMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.resolveSummary(orgId, summaryId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to resolve summary');
      }
      return res.data;
    },
    onSuccess: () => {
      setSelectedSummaryId(null);
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({ title: 'Summary marked as resolved' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to resolve summary',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.dismissSummary(orgId, summaryId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to dismiss summary');
      }
      return res.data;
    },
    onSuccess: () => {
      setSelectedSummaryId(null);
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({ title: 'Summary dismissed' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to dismiss summary',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ── Derived ─────────────────────────────────────────────────────────────

  const summaries = summariesData?.summaries ?? [];
  const totalSummaries = summariesData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalSummaries / PAGE_SIZE));
  const categories = report?.categories ?? [];
  const isLoading = summariesLoading;
  const isError = reportError || summariesError;

  const selectedSummary = summaries.find((s) => s.id === selectedSummaryId) ?? null;

  // Auto-select first summary when data loads
  useEffect(() => {
    if (summaries.length > 0 && !selectedSummaryId) {
      setSelectedSummaryId(summaries[0].id);
    }
  }, [summaries, selectedSummaryId]);

  // Reset selection when filters change
  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setPage(1);
    setSelectedSummaryId(null);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value as SortBy);
    setPage(1);
    setSelectedSummaryId(null);
  };

  // ── Error state ─────────────────────────────────────────────────────────

  if (isError && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          Failed to load knowledge gaps data.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetchReport();
            refetchSummaries();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Category</label>
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {categories.map((cat: KGReportCategory) => {
                const meta = getCategoryMeta(cat.category);
                return (
                  <SelectItem key={cat.category} value={cat.category}>
                    {meta.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Sort By</label>
          <Select value={sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Master-detail layout */}
      {isLoading ? (
        <div className="flex h-[calc(100vh-13rem)] rounded-xl border card-border card-bg overflow-hidden">
          <div className="w-[350px] shrink-0 border-r card-border p-3 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      ) : summaries.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No knowledge gaps detected"
          description="Run the pipeline to scan for gaps."
        />
      ) : (
        <div className="flex h-[calc(100vh-13rem)] rounded-xl border card-border card-bg overflow-hidden">
          {/* Left: summary list */}
          <div className="w-[350px] border-r card-border flex flex-col card-bg shrink-0">
            {/* Header */}
            <div className="px-4 py-3 min-h-[52px] border-b card-border flex items-center gap-3">
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-base font-medium">Gaps</h3>
              <span className="ml-auto card-secondary-bg text-sm px-2 py-0.5 rounded-md">
                {totalSummaries}
              </span>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {summaries.map((summary: KGSummaryResponse) => {
                const isSelected = selectedSummaryId === summary.id;
                const meta = getCategoryMeta(summary.category);

                return (
                  <div
                    key={summary.id}
                    className={cn(
                      'w-full rounded-xl border p-3 text-left transition cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm',
                      isSelected
                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                        : 'card-border card-secondary-bg',
                    )}
                    onClick={() => setSelectedSummaryId(summary.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">
                          {summary.topic}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {meta.label}
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className="shrink-0 text-xs"
                      >
                        {summary.occurrence_count}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t card-border px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      setSelectedSummaryId(null);
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      setSelectedSummaryId(null);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right: detail panel */}
          <div className="flex-1 flex flex-col card-bg overflow-hidden">
            {selectedSummary ? (
              <GapDetail
                orgId={orgId}
                summary={selectedSummary}
                onResolve={() => resolveMutation.mutate(selectedSummary.id)}
                onDismiss={() => dismissMutation.mutate(selectedSummary.id)}
                isResolving={
                  resolveMutation.isPending &&
                  resolveMutation.variables === selectedSummary.id
                }
                isDismissing={
                  dismissMutation.isPending &&
                  dismissMutation.variables === selectedSummary.id
                }
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Select a gap to view details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
